/**
 * Themed confirmation dialog — replaces the native window.confirm.
 * Locks to the app's dark-glass scheme so destructive actions get a
 * moment of friction that still feels premium.
 *
 * Usage (via useConfirmDialog):
 *   const { confirm, dialog } = useConfirmDialog();
 *   await confirm({ title, description, confirmLabel, tone: 'danger' });
 *   // render {dialog} once inside the page tree.
 */
import { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';

const TONE_STYLES = {
  danger: {
    accent: 'text-red-300',
    iconBg: 'bg-red-500/15 border-red-500/40',
    iconColor: 'text-red-400',
    confirmBtn:
      'bg-red-500 hover:bg-red-400 text-white shadow-[0_4px_14px_rgba(239,68,68,0.35)]',
    glow: 'from-red-500/20',
  },
  primary: {
    accent: 'text-primary',
    iconBg: 'bg-primary/15 border-primary/40',
    iconColor: 'text-primary',
    confirmBtn:
      'bg-primary hover:bg-primary/90 text-black shadow-[0_4px_14px_rgba(250,204,21,0.35)]',
    glow: 'from-primary/20',
  },
};

export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  onConfirm,
  onCancel,
}) => {
  const styles = TONE_STYLES[tone] || TONE_STYLES.primary;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="confirm-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onCancel}
          data-testid="confirm-dialog-backdrop"
        >
          <motion.div
            key="confirm-panel"
            initial={{ opacity: 0, scale: 0.94, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 4 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900 to-black shadow-[0_20px_60px_rgba(0,0,0,0.55)] overflow-hidden"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            data-testid="confirm-dialog"
          >
            <div
              className={`pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-gradient-to-br ${styles.glow} to-transparent blur-3xl`}
            />

            <button
              type="button"
              onClick={onCancel}
              className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-white hover:bg-white/10 transition-colors z-10"
              aria-label="Close"
              data-testid="confirm-dialog-close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative p-6">
              <div className="flex items-start gap-3 mb-4">
                <span
                  className={`h-10 w-10 rounded-full border flex items-center justify-center flex-shrink-0 ${styles.iconBg}`}
                >
                  <AlertTriangle className={`h-5 w-5 ${styles.iconColor}`} />
                </span>
                <div className="flex-1 min-w-0">
                  <h3
                    id="confirm-title"
                    className={`font-heading text-lg uppercase tracking-wider leading-tight ${styles.accent}`}
                  >
                    {title}
                  </h3>
                </div>
              </div>

              {description && (
                <p className="text-sm text-white/80 leading-relaxed mb-6">
                  {description}
                </p>
              )}

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={onCancel}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-medium text-white/80 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/25 transition-all"
                  data-testid="confirm-dialog-cancel"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  autoFocus
                  className={`inline-flex items-center justify-center px-5 py-2 rounded-full text-sm font-semibold uppercase tracking-wider transition-all ${styles.confirmBtn}`}
                  data-testid="confirm-dialog-confirm"
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * useConfirmDialog — promise-based imperative API so calling sites can just:
 *
 *    if (!(await confirm({ title, description }))) return;
 */
export const useConfirmDialog = () => {
  const [state, setState] = useState({ open: false });
  const resolveRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, ...opts });
    });
  }, []);

  const close = useCallback((result) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setState((s) => ({ ...s, open: false }));
  }, []);

  const dialog = (
    <ConfirmDialog
      {...state}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );

  return { confirm, dialog };
};

export default ConfirmDialog;
